// Owns every DOM node and global the respond.io loader creates, so the widget
// can be torn down and re-created bound to a different user within one page
// session.
import './types';

const SCRIPT_ID = 'respondio__widget';

// Any element whose id starts with this prefix belongs to the app, not to
// respond.io, and must survive teardown.
const APP_OWNED_PREFIX = 'respondio-';

// respond.io's own hosts, for a build that boots their loader from their CDN
// rather than ours.
const RESPOND_HOST_PATTERN = /respond\.io|rocketbots/i;

function scriptEl(): HTMLScriptElement | null {
  return document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
}

// The identity the booted widget is bound to: null when nothing is booted,
// '' when booted anonymously. Stamped on the tag rather than held in module
// state because a hot reload resets module state while the DOM survives.
export function bootedIdentity(): string | null {
  return scriptEl()?.dataset.respondIdentity ?? null;
}

// The origin the booted loader came from — the chat iframe is served from it.
// Read off the live tag, so it must be captured before the tag is removed.
function bootedOrigin(): string | null {
  const src = scriptEl()?.src;
  if (!src) {
    return null;
  }
  try {
    return new URL(src).origin;
  } catch {
    return null;
  }
}

export function injectLoader(src: string, identity: string): void {
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.dataset.respondIdentity = identity;
  // Cache-bust so re-injection re-executes rather than replaying a cached copy.
  script.src = `${src}${src.includes('?') ? '&' : '?'}_=${Date.now()}`;
  script.async = true;
  document.head.appendChild(script);
}

function isWidgetIframe(
  iframe: HTMLIFrameElement,
  origin: string | null,
): boolean {
  if (RESPOND_HOST_PATTERN.test(iframe.src)) {
    return true;
  }
  return origin !== null && iframe.src.startsWith(`${origin}/`);
}

// The body-level ancestor of `el` — the element the loader appended directly to
// <body> and rendered its widget inside.
function bodyLevelAncestor(el: Element): Element | null {
  let cur: Element | null = el;
  while (cur && cur.parentElement && cur.parentElement !== document.body) {
    cur = cur.parentElement;
  }
  return cur && cur.parentElement === document.body ? cur : null;
}

// Every container the loader mounted a chat iframe inside. Anchored on the
// iframe rather than on the container's id, which is a CSS-module content hash
// from respond.io's build and changes whenever they rebuild — and rather than
// on "everything added to <body> since injection", which sweeps up the app's
// own root siblings, modals and snackbars and blanks the page on teardown.
//
// The whole container must go, not just the iframe's immediate parent: a
// leftover container keeps its hashed id, and the loader resolves its mount
// point with getElementById, so the next boot would render into the orphan and
// never mount an iframe.
function widgetContainers(origin: string | null): Element[] {
  const found = new Set<Element>();

  document.querySelectorAll('iframe').forEach((iframe) => {
    if (!isWidgetIframe(iframe, origin)) {
      return;
    }
    const container = bodyLevelAncestor(iframe);
    if (container && !container.id.startsWith(APP_OWNED_PREFIX)) {
      found.add(container);
    }
  });

  return [...found];
}

export function removeWidgetNodes(): void {
  const origin = bootedOrigin();

  // Before the containers: the loader resolves its config with getElementById,
  // which returns the first match, so a leftover tag would shadow a new one.
  scriptEl()?.remove();
  widgetContainers(origin).forEach((el) => el.remove());

  delete window.$respond;
  delete window.$__respond;
  delete window.$__iframe;
}
